<template>
  <div
    ref="host"
    class="ngc-boolean-wrapper"
  />
</template>

<script>
const PROP_TO_ATTR = {
  label: 'label',
  helpText: 'help-text',
  errorText: 'error-text',
  disabled: 'disabled',
  required: 'required',
  optional: 'optional',
  skeleton: 'skeleton',
  hideRequired: 'hide-required'
}

export default {
  name: 'NgcBoolean',
  inheritAttrs: false,
  props: {
    value: {
      type: [Boolean, String],
      default: undefined
    },
    id: {
      type: String,
      default: ''
    },

    options: {
      type: Array,
      default: undefined
    },
    label: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    errorText: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    optional: {
      type: Boolean,
      default: false
    },
    skeleton: {
      type: Boolean,
      default: false
    },
    hideRequired: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      el: null,
      isDestroyed: false
    }
  },
  watch: {
    value (newVal) {
      if (!this.el) return
      this.el.value = newVal
    },
    id () { this.syncId() },
    options () { this.syncOptions() },
    label () { this.syncAttributes() },
    helpText () { this.syncAttributes() },
    errorText () { this.syncAttributes() },
    disabled () { this.syncAttributes() },
    required () { this.syncAttributes() },
    optional () { this.syncAttributes() },
    skeleton () { this.syncAttributes() },
    hideRequired () { this.syncAttributes() }
  },
  async mounted () {
    await customElements.whenDefined('ngc-boolean')
    if (this.isDestroyed) return
    // Bypass Vue's template compiler by creating the element imperatively
    this.el = document.createElement('ngc-boolean')
    this.syncId()
    this.syncAttributes()
    this.syncOptions()
    this.syncValue()
    this.el.addEventListener('change', this.handleChange)
    this.$refs.host.appendChild(this.el)
    await this.el.updateComplete
    this.syncValue()
  },
  beforeDestroy () {
    this.isDestroyed = true
    if (this.el) {
      this.el.removeEventListener('change', this.handleChange)
      this.el = null
    }
  },
  methods: {
    syncId () {
      if (!this.el) return
      if (this.id) {
        this.el.setAttribute('id', this.id)
      } else {
        this.el.removeAttribute('id')
      }
    },
    syncOptions () {
      if (!this.el || this.options === undefined) return
      this.el.options = this.options
    },
    syncAttributes () {
      if (!this.el) return
      for (const [propName, attrName] of Object.entries(PROP_TO_ATTR)) {
        const value = this[propName]
        if (typeof value === 'boolean') {
          if (value) this.el.setAttribute(attrName, '')
          else this.el.removeAttribute(attrName)
        } else if (value == null || value === '') {
          this.el.removeAttribute(attrName)
        } else {
          this.el.setAttribute(attrName, String(value))
        }
      }
    },
    syncValue () {
      if (!this.el) return
      this.el.value = this.value == null || this.value === '' ? undefined : this.value
    },
    coerceDetailForVue (raw) {
      if (raw === 'true') return true
      if (raw === 'false') return false
      return raw
    },
    handleChange (e) {
      if (!e) return
      const detail = this.coerceDetailForVue(e.detail)
      const forwarded = new CustomEvent('change', {
        bubbles: e.bubbles !== false,
        composed: !!e.composed,
        detail
      })
      this.$emit('change', forwarded)
    }
  }
}
</script>

<style lang="scss" scoped>
.ngc-boolean-wrapper {
  display: contents;
}
</style>
